import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, X, Save, Link2, Unplug, CircleDot, RefreshCw, Shield, Eye, EyeOff, KeyRound, ShieldCheck, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";

function RecipientChips({ recipients, onChange, testid }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v || recipients.includes(v)) return;
    onChange([...recipients, v]);
    setVal("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {recipients.map((r) => (
          <span key={r} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-xs" data-testid={`${testid}-chip-${r}`}>
            {r}<button onClick={() => onChange(recipients.filter((x) => x !== r))}><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={val} placeholder="name@company.com" onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`${testid}-input`} />
        <Button variant="outline" size="icon" onClick={add} data-testid={`${testid}-add`}><Plus size={14} /></Button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [email, setEmail] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [frCreds, setFrCreds] = useState({ auth_key: "", auth_token: "", base_url: "" });
  const [reps, setReps] = useState([]);
  const [goals, setGoals] = useState({});
  const [savingFr, setSavingFr] = useState(false);
  const [runningFr, setRunningFr] = useState(false);
  const [scheduler, setScheduler] = useState(null);

  const load = async () => {
    const [e, integ, r, g, sch] = await Promise.all([
      api.get("/settings/email"),
      api.get("/status/integrations"),
      api.get("/reps"),
      api.get("/settings/goals"),
      api.get("/sync/scheduler_status").catch(() => ({ data: null })),
    ]);
    setEmail(e.data);
    setIntegrations(integ.data);
    setReps(r.data.items || []);
    const gmap = {};
    for (const it of g.data.items || []) gmap[it.rep] = it.monthly_revenue_target;
    setGoals(gmap);
    setScheduler(sch.data);
  };
  useEffect(() => { load(); }, []);

  const saveEmail = async () => {
    try {
      await api.post("/settings/email", email);
      toast.success("Email settings saved");
    } catch (e) { toast.error(e.message); }
  };

  const saveFr = async () => {
    setSavingFr(true);
    try {
      await api.post("/settings/integrations/fieldroutes", frCreds);
      toast.success("FieldRoutes credentials saved");
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSavingFr(false); }
  };

  const runFrSync = async (full = false) => {
    setRunningFr(true);
    try {
      await api.post("/sync/fieldroutes", null, { params: { full } });
      toast.success(full ? "Full FR sync started" : "FR sync started");
      setTimeout(() => { load(); setRunningFr(false); }, 5000);
    } catch (e) { toast.error(e.message); setRunningFr(false); }
  };

  const saveGoal = async (rep) => {
    try {
      await api.post("/settings/goals", { rep, monthly_revenue_target: Number(goals[rep] || 0) });
      toast.success(`Goal saved for ${rep}`);
    } catch (e) { toast.error(e.message); }
  };

  if (!email) return <div className="space-y-4" data-testid="page-settings">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="page-settings">
      <div>
        <h1 className="font-display text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Integrations, email schedule, reps and goals.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-6" data-testid="settings-card-integrations">
          <CardHeader><CardTitle className="flex items-center gap-2 text-sm font-medium"><Link2 size={16} /> Integrations</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <IntegrationCard name="GoHighLevel" status={integrations?.ghl?.status} records={integrations?.ghl?.records} />
            <IntegrationCard name="FieldRoutes" status={integrations?.fieldroutes?.status} records={integrations?.fieldroutes?.records} mode={integrations?.fieldroutes?.mode} />
            <IntegrationCard name="Resend" status={integrations?.resend?.status} />

            {/* Background scheduler health — confirms auto-sync is alive in prod */}
            <div className="pt-3 mt-2 border-t border-border" data-testid="scheduler-health">
              <div className="text-sm font-medium mb-2 flex items-center gap-2">
                <RefreshCw size={14} />
                Background scheduler
                {scheduler && (
                  <Badge variant={scheduler.running ? "default" : "destructive"} className="ml-2">
                    {scheduler.running ? "Running" : "Stopped"}
                  </Badge>
                )}
              </div>
              {scheduler?.running ? (
                <div className="space-y-1 text-xs text-muted-foreground">
                  {scheduler.jobs?.map((j) => (
                    <div key={j.id} className="flex items-center justify-between gap-2">
                      <span className="text-foreground">{j.id.replace(/_/g, " ")}</span>
                      <span>next: {j.next_run ? new Date(j.next_run).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                    </div>
                  ))}
                  {scheduler.last_sync && (
                    <div className="pt-2 border-t border-border mt-2">
                      <div className="text-foreground">Last sync ({scheduler.last_sync.source})</div>
                      <div>{scheduler.last_sync.completed_at ? new Date(scheduler.last_sync.completed_at).toLocaleString() : "—"}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-destructive">Scheduler is not running. Auto-sync and recap emails will not fire. Contact support.</div>
              )}
            </div>

            <div className="pt-3 border-t border-border">
              <div className="text-sm font-medium mb-2 flex items-center gap-2"><Unplug size={14} /> Add FieldRoutes credentials</div>
              <div className="space-y-2">
                <Input placeholder="authenticationKey" value={frCreds.auth_key} onChange={(e) => setFrCreds({ ...frCreds, auth_key: e.target.value })} data-testid="fr-auth-key" />
                <Input placeholder="authenticationToken" value={frCreds.auth_token} onChange={(e) => setFrCreds({ ...frCreds, auth_token: e.target.value })} data-testid="fr-auth-token" />
                <Input placeholder="Base URL (optional) e.g. https://coastalwildlife.fieldroutes.com/api" value={frCreds.base_url} onChange={(e) => setFrCreds({ ...frCreds, base_url: e.target.value })} data-testid="fr-base-url" />
                <Button onClick={saveFr} disabled={savingFr || !frCreds.auth_key || !frCreds.auth_token} data-testid="fr-save"><Save size={14} className="mr-2" />Save credentials</Button>
                <div className="flex gap-2 pt-2 border-t border-border">
                  <Button size="sm" variant="outline" onClick={() => runFrSync(false)} disabled={runningFr || integrations?.fieldroutes?.status !== "live"} data-testid="fr-run-sync">
                    <RefreshCw size={14} className={runningFr ? "animate-spin mr-2" : "mr-2"} />Run FR Sync (recent)
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => runFrSync(true)} disabled={runningFr || integrations?.fieldroutes?.status !== "live"} data-testid="fr-run-full-sync">
                    <RefreshCw size={14} className={runningFr ? "animate-spin mr-2" : "mr-2"} />Full sync
                  </Button>
                </div>
                <div className="text-[11px] text-muted-foreground">Live FR sync activates in the next scheduler run after credentials are saved, or click the button above to run immediately.</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-6" data-testid="settings-card-email">
          <CardHeader><CardTitle className="text-sm font-medium">Recap emails</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Recipients (To)</div>
              <RecipientChips recipients={email.recipients || []} onChange={(v) => setEmail({ ...email, recipients: v })} testid="email-to" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">CC</div>
              <RecipientChips recipients={email.cc || []} onChange={(v) => setEmail({ ...email, cc: v })} testid="email-cc" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">BCC</div>
              <RecipientChips recipients={email.bcc || []} onChange={(v) => setEmail({ ...email, bcc: v })} testid="email-bcc" />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium">Daily recap</div>
                  <Switch checked={email.daily_enabled} onCheckedChange={(v) => setEmail({ ...email, daily_enabled: v })} data-testid="daily-enabled" />
                </div>
                <Input type="time" value={email.daily_time} onChange={(e) => setEmail({ ...email, daily_time: e.target.value })} data-testid="daily-time" />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Sent daily · recaps the previous calendar day's activity. Default 03:00 local.
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium">Weekly recap</div>
                  <Switch checked={email.weekly_enabled} onCheckedChange={(v) => setEmail({ ...email, weekly_enabled: v })} data-testid="weekly-enabled" />
                </div>
                <div className="flex gap-2">
                  <Select value={email.weekly_day} onValueChange={(v) => setEmail({ ...email, weekly_day: v })}>
                    <SelectTrigger className="w-[130px]" data-testid="weekly-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((d) => (<SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Input type="time" value={email.weekly_time} onChange={(e) => setEmail({ ...email, weekly_time: e.target.value })} data-testid="weekly-time" />
                </div>
              </div>
            </div>

            <Button onClick={saveEmail} data-testid="email-save"><Save size={14} className="mr-2" />Save email settings</Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-12" data-testid="settings-card-secrets">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield size={14} /> API Keys & Secrets
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <SecretsManager />
          </CardContent>
        </Card>

        <Card className="lg:col-span-12" data-testid="settings-card-users">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Users</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <UsersManager />
          </CardContent>
        </Card>

        <Card className="lg:col-span-12" data-testid="settings-card-goals">
          <CardHeader><CardTitle className="text-sm font-medium">Rep goals (monthly revenue target)</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Rep</TableHead><TableHead>Sources</TableHead><TableHead className="w-[260px]">Monthly revenue target</TableHead><TableHead className="w-24" /></TableRow></TableHeader>
              <TableBody>
                {reps.map((r) => (
                  <TableRow key={r.name}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(r.sources || []).join(", ")}</TableCell>
                    <TableCell>
                      <Input type="number" value={goals[r.name] ?? ""} placeholder="0" onChange={(e) => setGoals({ ...goals, [r.name]: e.target.value })} data-testid={`goal-input-${r.name.replaceAll(" ", "-").toLowerCase()}`} />
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => saveGoal(r.name)} data-testid={`goal-save-${r.name.replaceAll(" ", "-").toLowerCase()}`}>Save</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function UsersManager() {
  const [users, setUsers] = useState([]);
  const [reps, setReps] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "manager", linked_rep_name: "" });

  const load = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/reps")]);
      setUsers(u.data.items || []);
      setReps(r.data.items || []);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const body = { ...form };
      if (!body.linked_rep_name) delete body.linked_rep_name;
      await api.post("/users", body);
      toast.success("User created");
      setShow(false);
      setForm({ email: "", name: "", password: "", role: "manager", linked_rep_name: "" });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(`User ${u.active ? "deactivated" : "activated"}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-end p-3 border-b border-border">
        <Button size="sm" onClick={() => setShow((v) => !v)} data-testid="users-add-toggle">
          <Plus size={14} className="mr-2" />{show ? "Cancel" : "Add user"}
        </Button>
      </div>
      {show && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-6 gap-2 border-b border-border bg-[hsl(var(--surface-2))]/30">
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="users-new-email" />
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="users-new-name" />
          <Input type="password" placeholder="Password (8+ chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="users-new-password" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="users-new-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["admin", "manager", "rep", "viewer"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.linked_rep_name || "__none__"} onValueChange={(v) => setForm({ ...form, linked_rep_name: v === "__none__" ? "" : v })}>
            <SelectTrigger data-testid="users-new-rep"><SelectValue placeholder="Link to rep (optional)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— No rep link —</SelectItem>
              {reps.map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={create} disabled={!form.email || !form.name || form.password.length < 8} data-testid="users-create"><Save size={14} className="mr-2" />Create</Button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Linked rep</TableHead><TableHead>Status</TableHead><TableHead className="w-32" /></TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
              <TableCell className="text-sm font-medium">{u.email}</TableCell>
              <TableCell className="text-sm">{u.name}</TableCell>
              <TableCell className="text-xs capitalize">{u.role}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{u.linked_rep_name || "–"}</TableCell>
              <TableCell><span className={`text-[11px] px-2 py-0.5 rounded-full border ${u.active ? "text-[hsl(var(--positive))] border-[hsl(var(--positive))]/30 bg-[hsl(var(--positive))]/10" : "text-muted-foreground border-border bg-muted"}`}>{u.active ? "Active" : "Disabled"}</span></TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.email}`}>{u.active ? "Disable" : "Enable"}</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(u)} className="text-[hsl(var(--negative))]" data-testid={`user-delete-${u.email}`}>Delete</Button>
              </TableCell>
            </TableRow>
          ))}
          {!users.length && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No users yet</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function IntegrationCard({ name, status, records, mode }) {
  const ok = status === "live";
  const pending = status === "pending_credentials";
  const tone = ok ? "text-[hsl(var(--positive))] border-[hsl(var(--positive))]/30 bg-[hsl(var(--positive))]/10"
    : pending ? "text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10"
    : "text-muted-foreground border-border bg-muted";
  return (
    <div className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`integration-row-${name.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <CircleDot size={16} className={ok ? "text-[hsl(var(--positive))]" : pending ? "text-[hsl(var(--warning))]" : "text-muted-foreground"} />
        <div>
          <div className="font-medium text-sm">{name}</div>
          {records !== undefined && <div className="text-xs text-muted-foreground">{records?.toLocaleString?.() ?? records} records {mode === "seeded" ? "(seeded)" : ""}</div>}
        </div>
      </div>
      <span className={`px-2 py-0.5 rounded-full border text-[11px] ${tone}`}>{ok ? "Live" : pending ? "Pending Credentials" : "Not configured"}</span>
    </div>
  );
}

function SecretsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {name, label}
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [testResults, setTestResults] = useState({}); // {group: {ok, detail, when}}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/secrets");
      setItems(data.items || []);
    } catch (e) {
      toast.error(`Failed to load secrets: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing || !value) return;
    setSaving(true);
    try {
      await api.post("/secrets", { name: editing.name, value });
      toast.success(`${editing.label} rotated`);
      setEditing(null);
      setValue("");
      setShow(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const revert = async (name, label) => {
    if (!confirm(`Delete stored ${label}? The app will fall back to the environment variable.`)) return;
    try {
      await api.delete(`/secrets/${encodeURIComponent(name)}`);
      toast.success(`${label} cleared — using environment fallback`);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const test = async (group) => {
    setTestResults({ ...testResults, [group]: { loading: true } });
    try {
      const { data } = await api.post("/secrets/test", null, { params: { group } });
      setTestResults({ ...testResults, [group]: { ...data, when: new Date() } });
      if (data.ok) toast.success(`${group.toUpperCase()}: ${data.detail}`);
      else toast.error(`${group.toUpperCase()}: ${data.detail}`);
    } catch (e) {
      setTestResults({ ...testResults, [group]: { ok: false, detail: e.message, when: new Date() } });
      toast.error(e.message);
    }
  };

  // Group by spec.group
  const groups = items.reduce((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s);
    return acc;
  }, {});
  const groupLabels = { ghl: "GoHighLevel", fr: "FieldRoutes", resend: "Resend Email", llm: "LLM", other: "Other" };
  const groupOrder = ["ghl", "fr", "resend", "llm", "other"];

  return (
    <div className="p-4 space-y-6">
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading secrets…</div>
      ) : (
        groupOrder.filter((g) => groups[g]).map((g) => {
          const r = testResults[g];
          return (
            <div key={g} className="border border-border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-[hsl(var(--surface-2))]/40 border-b border-border">
                <div className="flex items-center gap-2">
                  <KeyRound size={14} className="text-muted-foreground" />
                  <span className="text-sm font-medium">{groupLabels[g] || g}</span>
                  {r && (
                    <Badge
                      variant="outline"
                      className={r.ok ? "bg-[hsl(var(--positive))]/10 text-[hsl(var(--positive))] border-[hsl(var(--positive))]/30" : "bg-[hsl(var(--negative))]/10 text-[hsl(var(--negative))] border-[hsl(var(--negative))]/30"}
                    >
                      {r.ok ? <ShieldCheck size={10} className="mr-1" /> : <ShieldAlert size={10} className="mr-1" />}
                      {r.ok ? "Verified" : "Auth failed"}
                    </Badge>
                  )}
                </div>
                {(g === "ghl" || g === "fr" || g === "resend") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => test(g)}
                    disabled={r?.loading}
                    data-testid={`secret-test-${g}`}
                  >
                    {r?.loading ? <RefreshCw size={12} className="animate-spin mr-2" /> : <ShieldCheck size={12} className="mr-2" />}
                    Test connection
                  </Button>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Key</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead className="w-[140px]">Source</TableHead>
                    <TableHead className="w-[180px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups[g].map((s) => (
                    <TableRow key={s.name} data-testid={`secret-row-${s.name}`}>
                      <TableCell>
                        <div className="text-sm font-medium">{s.label}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{s.name}</div>
                      </TableCell>
                      <TableCell>
                        {s.configured ? (
                          <span className="font-mono text-xs tabular-nums">{s.masked}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {s.source === "database" ? (
                          <Badge variant="outline" className="bg-[hsl(var(--positive))]/10 text-[hsl(var(--positive))] border-[hsl(var(--positive))]/30">
                            <ShieldCheck size={10} className="mr-1" /> Encrypted
                          </Badge>
                        ) : s.source === "environment" ? (
                          <Badge variant="outline" className="text-muted-foreground border-border">
                            .env
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setEditing(s); setValue(""); setShow(false); }}
                          data-testid={`secret-rotate-${s.name}`}
                        >
                          <RefreshCw size={12} className="mr-1" />
                          Rotate
                        </Button>
                        {s.source === "database" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => revert(s.name, s.label)}
                            className="text-[hsl(var(--negative))]"
                            data-testid={`secret-clear-${s.name}`}
                          >
                            Clear
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          );
        })
      )}

      <div className="text-[11px] text-muted-foreground flex items-start gap-2 px-1">
        <Shield size={14} className="mt-0.5 flex-shrink-0" />
        <div>
          <div>
            Secrets are encrypted with AES-128 (Fernet) using a master key derived from <code className="text-[10px] bg-[hsl(var(--surface-3))] px-1 rounded">APP_SECRET_KEY</code>
            or a random persisted token. Stored values override environment variables.
          </div>
          <div className="mt-1">
            Values are never returned in full — only the last 4 characters are shown in the UI.
          </div>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="secret-rotate-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound size={16} /> Rotate {editing.label}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  Paste the new value below. It'll be encrypted and stored in the database. The previous value will be immediately replaced across all services.
                </div>
                <div className="flex gap-2">
                  <Input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={`New ${editing.label}`}
                    autoFocus
                    className="font-mono text-sm"
                    data-testid="secret-value-input"
                  />
                  <Button variant="outline" size="icon" onClick={() => setShow(!show)} title={show ? "Hide" : "Show"}>
                    {show ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                </div>
                {editing.public && (
                  <div className="text-[11px] text-muted-foreground">
                    Note: this value is a non-sensitive identifier (not a secret), but still encrypted at rest for auditability.
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving || !value} data-testid="secret-save">
                  {saving ? <RefreshCw size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                  Save encrypted
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

